// AnalyticsIdentityBridge
//
// Mounted once under the existing context providers in Appin.js.
// Watches auth + catalog state and pushes identity changes into Analytics.js.
// Renders nothing.
//
// Note: this component only mounts in Appin.js's main authenticated return path.
// During the brief auth-token-resolve window (where Appin returns an empty
// fragment before forcing window.location.href reload), the bridge is unmounted
// and any autocapture/pageview events fire under the anonymous distinct_id.
// Identity is re-established on the next page load. Acceptable per spec.

import { useContext, useEffect, useRef } from 'react';
import { useAuth } from '../auth/useAuth';
import CatalogContext from '../contexts/CatalogContext';
import { identifyUser, registerCatalog, resetAnalytics } from './Analytics';

const AnalyticsIdentityBridge = () => {
	const { currentUser } = useAuth();
	const catalogTuple = useContext(CatalogContext);
	const catalogConfig = Array.isArray(catalogTuple) ? catalogTuple[0] : catalogTuple?.catalogConfig;

	const lastUserIdRef = useRef(null);
	const lastCatalogIdRef = useRef(null);

	// Identity: identify on login, reset on logout.
	// `identifyUser` is async (Web Crypto SHA-256). Analytics.js catches its own
	// internal failures, but we still attach a `.catch` here to silence any
	// unhandled-rejection warning if the promise pipeline itself misbehaves.
	useEffect(() => {
		const userId = currentUser?.id;
		if (userId && userId !== lastUserIdRef.current) {
			lastUserIdRef.current = userId;
			identifyUser(userId).catch(() => {
				// Already logged inside Analytics.js; swallow to avoid unhandled rejection.
			});
		} else if (!userId && lastUserIdRef.current) {
			lastUserIdRef.current = null;
			resetAnalytics();
		}
	// Dep is `id` only — re-firing on full `currentUser` ref churn would identify
	// the same user repeatedly. The id is the only field whose change matters here.
	}, [currentUser?.id]);

	// Catalog: register super-properties on catalog change.
	// Dep is `id` only — `name` / `organization_id` are effectively immutable for a
	// given catalog, and depending on the full object would re-fire `register` on every
	// parent render. If catalog rename ever needs analytics accuracy, switch to a
	// stable equality check rather than expanding the dep array.
	useEffect(() => {
		const catalogId = catalogConfig?.id;
		if (catalogId && catalogId !== lastCatalogIdRef.current) {
			lastCatalogIdRef.current = catalogId;
			registerCatalog(catalogConfig);
		}
	// Dep is `id` only — see comment above the effect for the full reasoning.
	}, [catalogConfig?.id]);

	return null;
};

export default AnalyticsIdentityBridge;
